"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getShades = exports.contrast = exports.luminance = exports.brightness = exports.shade = exports.tint = exports.getRgb = exports.isLight = exports.getGray = exports.getSecondaryColor = exports.getComplementaryColor = exports.colorLuminance = exports.colorContrast = exports.colorBrightness = exports.colorAlpha = exports.createColor = exports.basicShades = exports.isColor = void 0;
const color_1 = __importDefault(require("color"));
const hsluv_1 = require("hsluv");
const __1 = require("..");
const collections_1 = require("../collections");
function isColor(color) {
    return Boolean(color &&
        typeof color === 'object' &&
        color.shades &&
        color.__emotion_styles);
}
exports.isColor = isColor;
exports.basicShades = [
    'deep',
    'dark',
    'semidark',
    'semilight',
    'light',
    'intense',
];
function findContrastingShade(color, shades, lvl) {
    return (0, collections_1.findClosest)(lvl, shades, (c) => c.contrast(color));
}
function createColorWithShades(color, shades = {}) {
    const getColor = () => color;
    const self = new Proxy(Object.assign(String(color), (0, collections_1.omit)(shades, 0)), {
        get: (obj, key) => {
            switch (key) {
                case '__emotion_styles': {
                    return true;
                }
                case 'toString': {
                    return getColor;
                }
                case 'shades': {
                    return Object.assign(Object.assign({}, shades), { 0: self });
                }
                case 'alpha': {
                    return (level) => colorAlpha(color, level);
                }
                case 'brightness': {
                    return (lvl) => typeof lvl === 'number'
                        ? colorBrightness(color, lvl)
                        : brightness(color);
                }
                case 'luminance': {
                    return () => luminance(color);
                }
                case 'contrast': {
                    return (bg, lvl) => typeof lvl === 'number'
                        ? colorContrast(color, bg, lvl)
                        : contrast(color, bg);
                }
                case 'getContrasting': {
                    return (source, lvl) => {
                        if (isColor(source)) {
                            const sh = Object.values(source.shades);
                            if (lvl !== undefined) {
                                return typeof lvl === 'number'
                                    ? findContrastingShade(color, sh, lvl)
                                    : source.contrast(color, lvl.exactly);
                            }
                            const cShade = findContrastingShade(color, sh, 24);
                            return contrast(cShade, color) >= 4.5
                                ? cShade
                                : colorContrast(source, color);
                        }
                        const contrasting = isLight(color) ? '#000' : '#fff';
                        if (lvl !== undefined) {
                            const currentLevel = typeof lvl === 'number' ? lvl : lvl.exactly;
                            return colorContrast(source !== null && source !== void 0 ? source : contrasting, color, currentLevel);
                        }
                        return source ? colorContrast(source, color) : contrasting;
                    };
                }
                default: {
                    const value = obj[key];
                    return typeof value === 'function' ? value.bind(obj) : value;
                }
            }
        },
    });
    return self;
}
function createColor(color, shades = {}) {
    const colorShades = Object.keys(shades).reduce((acc, sh) => Object.assign(acc, { [sh]: createColorWithShades(shades[sh]) }), {});
    const defaultShades = getShades(color);
    exports.basicShades.forEach((sh, i) => {
        if (!colorShades[sh]) {
            colorShades[sh] = createColorWithShades(defaultShades[i]);
        }
    });
    return createColorWithShades(color, colorShades);
}
exports.createColor = createColor;
function hsluv(color) {
    const { r, g, b } = getRgb(color);
    const [h, s, l] = (0, hsluv_1.rgbToHsluv)([r, g, b].map((v) => v / 255));
    return [h, s, l / 100];
}
function hex(...args) {
    if (args.length === 3) {
        const [h, s, l] = args;
        return (0, hsluv_1.hsluvToHex)([h, s, l * 100]);
    }
    return (0, color_1.default)(args[0]).hex();
}
function colorAlpha(color, level) {
    return (0, color_1.default)(color.toString()).alpha(level).toString();
}
exports.colorAlpha = colorAlpha;
function colorBrightness(color, l) {
    const [h, s] = hsluv(color);
    return hex(h, s, l);
}
exports.colorBrightness = colorBrightness;
function colorContrast(color, bg, lvl = 4.5) {
    const current = contrast(color, bg);
    const bgLum = luminance(bg);
    const cLum = luminance(color);
    const auto = lvl === undefined;
    if (isLight(bg)) {
        if (cLum < bgLum && current > lvl && auto) {
            return color;
        }
        const lum = (bgLum + 0.05 - 0.05 * lvl) / lvl;
        return colorLuminance(color, lum, 'tint');
    }
    if (cLum > bgLum && current > lvl && auto) {
        return color;
    }
    const lum = lvl * (bgLum + 0.05) - 0.05;
    return colorLuminance(color, lum, 'tint');
}
exports.colorContrast = colorContrast;
function colorLuminance(color, lvl, strategy = 'brightness') {
    const cappedLvl = (0, __1.minmax)(lvl, 0, 1, 2);
    if (strategy === 'brightness') {
        const [h, s] = hsluv(color);
        let l = cappedLvl;
        let lum = luminance(hex(h, s, l));
        let diff = lum < cappedLvl ? 1 - cappedLvl : cappedLvl;
        while ((0, __1.round)(lum, 2) !== cappedLvl) {
            diff /= 2;
            l += lum > cappedLvl ? -diff : diff;
            lum = luminance(hex(h, s, l));
        }
        return hex(h, s, l);
    }
    const brighten = luminance(color) < cappedLvl;
    const modifier = brighten ? tint : shade;
    let factor = 0.5;
    let diff = 0.5;
    let lum = luminance(modifier(color, factor));
    while ((0, __1.round)(lum, 2) !== cappedLvl) {
        diff /= 2;
        factor += (lum > cappedLvl ? -diff : diff) * (brighten ? 1 : -1);
        lum = luminance(modifier(color, factor));
    }
    return modifier(color, factor);
}
exports.colorLuminance = colorLuminance;
function getComplementaryColor(color) {
    const c = (0, color_1.default)(color.toString());
    return createColor(c.rotate(180).hex());
}
exports.getComplementaryColor = getComplementaryColor;
function getSecondaryColor(color) {
    const c = (0, color_1.default)(color.toString());
    const [darker, brighter] = [c.rotate(-20).hex(), c.rotate(20).hex()].sort((a, b) => brightness(a) - brightness(b));
    return createColor(brightness(color) > 0.6 ? darker : brighter);
}
exports.getSecondaryColor = getSecondaryColor;
function getGray(color) {
    return createColor(colorBrightness((0, color_1.default)(color.toString()).saturationv(10).hex(), 0.6));
}
exports.getGray = getGray;
const MID_LUMINANCE = 0.1833;
function isLight(color) {
    return luminance(color) > MID_LUMINANCE;
}
exports.isLight = isLight;
function getRgb(color) {
    return (0, collections_1.pick)((0, color_1.default)(color.toString()).rgb().object(), 'r', 'g', 'b');
}
exports.getRgb = getRgb;
function tint(color, factor) {
    return (0, color_1.default)((0, collections_1.map)((lvl) => lvl + (255 - lvl) * factor, getRgb(color))).hex();
}
exports.tint = tint;
function shade(color, factor) {
    return (0, color_1.default)((0, collections_1.map)((lvl) => lvl * (1 - factor), getRgb(color))).hex();
}
exports.shade = shade;
function brightness(color) {
    return hsluv(color)[2];
}
exports.brightness = brightness;
function luminance(color) {
    const { r, g, b } = getRgb(color);
    const [R, G, B] = [r, g, b].map((v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow(((s + 0.055) / 1.055), 2.4);
    });
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}
exports.luminance = luminance;
function contrast(color1, color2) {
    const [dark, light] = [color1, color2].map(luminance).sort((a, b) => a - b);
    return (light + 0.05) / (dark + 0.05);
}
exports.contrast = contrast;
function vector(...coordinates) {
    return Math.sqrt(coordinates.reduce((sum, c) => sum + Math.pow(c, 2), 0));
}
function rgbDiff(color1, color2) {
    const c1 = getRgb(color1);
    const c2 = getRgb(color2);
    return vector(c1.r - c2.r, c1.g - c2.g, c1.b - c2.b) / vector(255, 255, 255);
}
function colorJump(distance, n, edgeColor = 0.2, maxJump = distance) {
    const rgbJump = Math.min(Math.max(distance / (n + 1), (distance - edgeColor / 2) / n), maxJump);
    return Math.round((100 * rgbJump) / (distance || 1)) / 100;
}
function getShades(color, n = 3, edgeColor, maxJump) {
    const shadeJump = colorJump(rgbDiff('black', color), n, edgeColor, maxJump);
    const tintJump = colorJump(rgbDiff('white', color), n, edgeColor, maxJump);
    return [
        ...(0, collections_1.range)(1, n + 1)
            .map((i) => shade(color, i * shadeJump))
            .reverse(),
        ...(0, collections_1.range)(1, n + 1).map((i) => tint(color, i * tintJump)),
    ];
}
exports.getShades = getShades;
